import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import * as XLSX from 'xlsx';
import { UploadFileRequest } from '../models/client-reconciliation';

@Injectable({
    providedIn: 'root',
})
export class ReconciliationFileService {
    constructor(private http: HttpClient) { }
    private processJsonRecordsConnexPay(jsonRecords: any[]): any[] {
        return jsonRecords.filter((record)=>{
            return record.hasOwnProperty('ClientNameAlias') &&
            record.hasOwnProperty('Amount') &&
            record.hasOwnProperty('MerchantName') &&
            record.hasOwnProperty('ReleasedDate');
        });
    }

    private processJsonRecordsBrainTree(jsonRecords: any[]): any[] {
        return jsonRecords.filter((record)=>{
            return record.hasOwnProperty('Transaction ID') &&
            record.hasOwnProperty('Amount Authorized') &&
            record.hasOwnProperty('Cardholder Name') &&
            record.hasOwnProperty('Merchant Account');
        });

    }

    private removeSpacesFromKeys(obj: Record<string, unknown>) {
        const newObj: Record<string, unknown> = {};
        for (let key in obj) {
          if (obj.hasOwnProperty(key)) {
            const newKey = key.replace(/\s+/g, '');
            newObj[newKey] = obj[key];
          }
        }
        return newObj;
    }
    
    private readMappingFileConnexPayContent(file: File): Observable<any[]> {
        return new Observable<any[]>((observer) => {
            const fileReader = new FileReader();

            fileReader.onload = () => {
                const data = fileReader.result;
                const workBook = XLSX.read(data, { type: 'binary' });

                const jsonData = workBook.SheetNames.reduce((initial: any, name) => {
                    const sheet = workBook.Sheets[name];
                    let rawData = XLSX.utils.sheet_to_json(sheet, { raw: true });
                    rawData = rawData.map((row: any) => {
                        row['ReleasedDate'] = XLSX.SSF.format('yyyy-mm-dd hh:mm:ss', row['ReleasedDate']);
                        row['Date'] = XLSX.SSF.format('yyyy-mm-dd hh:mm:ss', row['Date']);
                        row['DateTime'] = XLSX.SSF.format('yyyy-mm-dd hh:mm:ss', row['DateTime']);
                        row['ITC'] = `${row['ITC']}`;
                        row['TicketNumber'] = `${row['TicketNumber']}`;
                        delete row['IssuedAmount'];
                        delete row['TicketNumber'];
                        return row;
                    });
                    initial[name] = rawData;
                    return initial;
                }, {});

                const filteredJsonData: any[] = [];
                Object.entries(jsonData).forEach((sheet)=>{
                    if(!sheet[0].includes('Summary')){
                        const filter = this.processJsonRecordsConnexPay(sheet[1] as any[])

                        filteredJsonData.push(...filter);
                    }
                })

                observer.next(filteredJsonData);
                observer.complete();
            };

            fileReader.onerror = () => {
                observer.error('Error reading file...');
            };

            fileReader.readAsBinaryString(file);
        });
    }

    private readMappingFileBrainTreeContent(file: File): Observable<any[]> {
        return new Observable<any[]>((observer) => {
            const fileReader = new FileReader();

            fileReader.onload = () => {
                const data = fileReader.result;
                const workBook = XLSX.read(data, { type: 'binary' });
                
                const jsonData = workBook.SheetNames.reduce((initial: any, name) => {
                    const sheet = workBook.Sheets[name];
                    let rawData = XLSX.utils.sheet_to_json(sheet, { raw: true });
                    rawData = rawData.map((row: any) => {
                        row['Created Datetime'] = XLSX.SSF.format('mm/dd/yyyy hh:mm:ss', row['Expiration Date']);
                        row['Settlement Date'] = XLSX.SSF.format('mm/dd/yyyy', row['Expiration Date']);
                        row['Expiration Date'] = XLSX.SSF.format('dd-mmm', row['Expiration Date']);
                        row['Tax Exempt'] = `${row['Tax Exempt']}`;
                        row['First Six of Credit Card'] = `${row['First Six of Credit Card']}`;
                        row['Last Four of Credit Card'] = `${row['Last Four of Credit Card']}`;
                        row['Processor Response Code'] = `${row['Processor Response Code']}`;
                        row['Transaction Type'] = `${row['Transaction Type']}`;
                        row['Billing Postal Code'] = `${row['Billing Postal Code']}`;
                        row['Authorization Code'] = `${row['Authorization Code']}`;
                        row['Acquirer Reference Number'] = `${row['Acquirer Reference Number']}`;
                        return row;
                    });
                    initial[name] = rawData;
                    return initial;
                }, {});

                const filteredJsonData: any[] = [];
                Object.entries(jsonData).forEach((sheet)=>{
                    const filter = this.processJsonRecordsBrainTree(sheet[1] as any[])
                    filteredJsonData.push(...filter);
                })

                const cleanedData = filteredJsonData.map(this.removeSpacesFromKeys);

                observer.next(cleanedData);
                observer.complete();
            };

            fileReader.onerror = () => {
                observer.error('Error reading file...');
            };

            fileReader.readAsBinaryString(file);
        });
    }

    sendFileContent(request: UploadFileRequest): Observable<any> {

        let seviceCall$: Observable<any>;
        if(request.ccProcessorId === 1) {
            seviceCall$ = this.readMappingFileConnexPayContent(request.file.file);
        }
        if(request.ccProcessorId === 2) {
            seviceCall$ = this.readMappingFileBrainTreeContent(request.file.file);
        }

        return seviceCall$!.pipe(switchMap((records)=>{
            const uploadPayload = {
                CcProcessorId: request.ccProcessorId,
                Records: records
            };

            return this.http.post('dbov2/reconciliation-data/upload-file-merge-records', {
                ...uploadPayload
            });
        }))

    }

}
